import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Settings, Save, Upload, Loader2, Eye, Palette, Bell, Map, 
  Truck, DollarSign, ShoppingBag, Clock, Link2, Image, 
  ExternalLink, CheckCircle, X, Plus, Trash2, Search,
  ArrowLeft, Coins
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TrackingSettingsPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    brand_name: '',
    logo_url: '',
    logo_redirect_url: '',
    announcement_enabled: false,
    announcement_text: '',
    announcement_link: '',
    announcement_bg_color: '#1a1a2e',
    announcement_text_color: '#ffffff',
    timeline_enabled: true,
    map_tracking_enabled: true,
    driver_details_enabled: true,
    tip_enabled: false,
    tip_slabs: [5, 10, 20, 50],
    tip_currency: 'AED',
    tip_payment_gateway: 'stripe',
    crosssell_enabled: false,
    crosssell_product_ids: [],
    crosssell_headline: 'You Might Also Like',
    crosssell_timer_enabled: false,
    crosssell_timer_duration: 30,
    celebration_enabled: true,
    social_enabled: true,
    social_instagram: '',
    social_facebook: '',
    social_tiktok: '',
    social_whatsapp: '',
    social_twitter: '',
    social_youtube: '',
    social_pinterest: '',
    cashback_enabled: false,
    cashback_percentage: 0,
    cashback_max: 0
  });
  
  // Product selection modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductDetails, setSelectedProductDetails] = useState([]); // Full details for display
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Stripe status
  const [stripeStatus, setStripeStatus] = useState({ configured: false });
  
  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/tracking/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setSettings(prev => ({
        ...prev,
        ...data,
        crosssell_product_ids: data.crosssell_product_ids || [],
        crosssell_headline: data.crosssell_headline || 'You Might Also Like',
        crosssell_timer_enabled: data.crosssell_timer_enabled || false,
        crosssell_timer_duration: data.crosssell_timer_duration || 30,
        celebration_enabled: data.celebration_enabled !== false,
        tip_slabs: data.tip_slabs || [5, 10, 20, 50],
        tip_currency: data.tip_currency || 'AED',
        tip_payment_gateway: data.tip_payment_gateway || 'stripe'
      }));
      
      // Fetch product details for selected cross-sell products.
      // Hydrate by explicit ids — paginating through the catalogue and
      // filtering in JS would silently drop selections whose product
      // doesn't fall in the first N rows, leaving the UI rendering only
      // the UUID for older selections (the bug we're fixing here).
      if (data.crosssell_product_ids && data.crosssell_product_ids.length > 0) {
        const productsResponse = await axios.get(`${API_BASE_URL}/products`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { ids: data.crosssell_product_ids.join(',') }
        });
        setSelectedProductDetails(productsResponse.data.products || []);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchStripeStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/tracking/stripe/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
    }
  }, []);
  
  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100, search: productSearch }
      });
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  }, [productSearch]);
  
  useEffect(() => {
    fetchSettings();
    fetchStripeStatus();
  }, [fetchSettings, fetchStripeStatus]);
  
  useEffect(() => {
    if (showProductModal) {
      fetchProducts();
    }
  }, [showProductModal, fetchProducts]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      Object.entries(settings).forEach(([key, value]) => {
        if (key === 'crosssell_product_ids') {
          formData.append(key, value.join(','));
        } else if (key === 'tip_slabs') {
          formData.append(key, Array.isArray(value) ? value.join(',') : value);
        } else if (typeof value === 'boolean') {
          formData.append(key, value.toString());
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      
      await axios.put(`${API_BASE_URL}/tracking/settings`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await axios.post(`${API_BASE_URL}/tracking/settings/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };
  
  const handleAddProducts = () => {
    // Get the new product IDs
    const newProductIds = [...new Set([...settings.crosssell_product_ids, ...selectedProducts])];
    
    // Update settings with new product IDs
    setSettings(prev => ({
      ...prev,
      crosssell_product_ids: newProductIds
    }));
    
    // Update selectedProductDetails with the newly added products
    const newProductDetails = products.filter(p => selectedProducts.includes(p.id));
    setSelectedProductDetails(prev => {
      const existingIds = prev.map(p => p.id);
      const newOnes = newProductDetails.filter(p => !existingIds.includes(p.id));
      return [...prev, ...newOnes];
    });
    
    setSelectedProducts([]);
    setShowProductModal(false);
  };
  
  const handleRemoveProduct = (productId) => {
    setSettings(prev => ({
      ...prev,
      crosssell_product_ids: prev.crosssell_product_ids.filter(id => id !== productId)
    }));
    // Also remove from selectedProductDetails
    setSelectedProductDetails(prev => prev.filter(p => p.id !== productId));
  };
  
  const getPreviewUrl = () => {
    return `${window.location.origin}/track`;
  };
  
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-wt-gold" />
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="text-wt-text-muted hover:text-wt-text-primary"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-wt-text-primary">Tracking Page Settings</h1>
            <p className="text-wt-text-muted text-sm">Configure your customer-facing order tracking page</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(getPreviewUrl(), '_blank')}
            className="border-wt-border text-wt-text-primary"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-wt-gold hover:bg-wt-gold-dark text-black"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>
      
      {/* Branding Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-wt-surface border border-wt-border rounded-xl p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Image className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-wt-text-primary">Branding</h2>
            <p className="text-sm text-wt-text-muted">Customize your tracking page appearance</p>
          </div>
        </div>
        
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-wt-text-primary">Brand Name</Label>
            <Input
              value={settings.brand_name || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, brand_name: e.target.value }))}
              placeholder="Your Company Name"
              className="bg-wt-bg border-wt-border text-wt-text-primary"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-wt-text-primary">Logo Redirect URL</Label>
            <Input
              value={settings.logo_redirect_url || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, logo_redirect_url: e.target.value }))}
              placeholder="https://yourwebsite.com"
              className="bg-wt-bg border-wt-border text-wt-text-primary"
            />
            <p className="text-xs text-wt-text-muted">When clicked, logo redirects here</p>
          </div>
          
          <div className="md:col-span-2 space-y-2">
            <Label className="text-wt-text-primary">Logo</Label>
            <div className="flex items-center gap-4">
              {settings.logo_url ? (
                <div className="relative group">
                  <img 
                    src={settings.logo_url} 
                    alt="Logo" 
                    className="h-16 w-auto object-contain bg-slate-800 rounded-lg p-2"
                  />
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, logo_url: '' }))}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <div className="h-16 w-32 bg-slate-800 rounded-lg flex items-center justify-center text-wt-text-muted">
                  No logo
                </div>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  className="border-wt-border text-wt-text-primary"
                  disabled={uploadingLogo}
                  asChild
                >
                  <span>
                    {uploadingLogo ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload Logo
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Announcement Bar Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-wt-surface border border-wt-border rounded-xl p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Bell className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-wt-text-primary">Announcement Bar</h2>
              <p className="text-sm text-wt-text-muted">Display promotions and notices</p>
            </div>
          </div>
          <Switch
            checked={settings.announcement_enabled}
            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, announcement_enabled: checked }))}
          />
        </div>
        
        {settings.announcement_enabled && (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2 space-y-2">
              <Label className="text-wt-text-primary">Announcement Text</Label>
              <Input
                value={settings.announcement_text || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, announcement_text: e.target.value }))}
                placeholder="Get 10% off your next order!"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Link URL (optional)</Label>
              <Input
                value={settings.announcement_link || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, announcement_link: e.target.value }))}
                placeholder="https://yourstore.com/offer"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Background</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.announcement_bg_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, announcement_bg_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.announcement_bg_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, announcement_bg_color: e.target.value }))}
                    className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Text Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.announcement_text_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, announcement_text_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.announcement_text_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, announcement_text_color: e.target.value }))}
                    className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Preview */}
            <div className="md:col-span-2">
              <Label className="text-wt-text-primary mb-2 block">Preview</Label>
              <div 
                className="rounded-lg py-2 px-4 text-center text-sm"
                style={{ 
                  backgroundColor: settings.announcement_bg_color,
                  color: settings.announcement_text_color
                }}
              >
                {settings.announcement_text || 'Your announcement text here'}
              </div>
            </div>
          </div>
        )}
      </motion.div>
      
      {/* Module Toggles */}
      {/* Social Links */}
      <div className="bg-wt-surface border border-wt-border rounded-xl p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-wt-text-primary">Social Media Links</h2>
          <p className="text-sm text-wt-text-muted">Shown as "Follow us" icons on the tracking page footer</p>
        </div>
        <div className="flex items-center justify-between mb-4">
          <Label>Show social links</Label>
          <Switch
            checked={settings.social_enabled !== false}
            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, social_enabled: checked }))}
            data-testid="social-enabled-switch"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['social_instagram', 'Instagram URL', 'https://instagram.com/yourbrand'],
            ['social_facebook', 'Facebook URL', 'https://facebook.com/yourbrand'],
            ['social_tiktok', 'TikTok URL', 'https://tiktok.com/@yourbrand'],
            ['social_whatsapp', 'WhatsApp (number or channel link)', '+9715xxxxxxxx or https://whatsapp.com/channel/...'],
            ['social_twitter', 'X (Twitter) URL', 'https://x.com/yourbrand'],
            ['social_youtube', 'YouTube URL', 'https://youtube.com/@yourbrand'],
            ['social_pinterest', 'Pinterest URL', 'https://pinterest.com/yourbrand'],
          ].map(([key, label, ph]) => (
            <div key={key}>
              <Label className="text-xs text-wt-text-muted">{label}</Label>
              <Input
                value={settings[key] || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={ph}
                className="mt-1"
                data-testid={`${key}-input`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Cashback Banner */}
      <div className="bg-wt-surface border border-wt-border rounded-xl p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-wt-text-primary">Cashback Banner</h2>
          <p className="text-sm text-wt-text-muted">
            Promise cashback on the tracking page. Keep the % in sync with your journey's "Add Store Credit" node —
            the banner flips to "Cashback added 🎉" automatically once the credit lands in the customer's wallet.
          </p>
        </div>
        <div className="flex items-center justify-between mb-4">
          <Label>Show cashback banner</Label>
          <Switch
            checked={!!settings.cashback_enabled}
            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, cashback_enabled: checked }))}
            data-testid="cashback-enabled-switch"
          />
        </div>
        {settings.cashback_enabled && (
          <div className="flex gap-4">
            <div className="w-40">
              <Label className="text-xs text-wt-text-muted">Cashback %</Label>
              <Input
                type="number" min="0" step="0.5"
                value={settings.cashback_percentage ?? ''}
                onChange={(e) => setSettings(prev => ({ ...prev, cashback_percentage: e.target.value }))}
                className="mt-1"
                data-testid="cashback-percentage-input"
              />
            </div>
            <div className="w-48">
              <Label className="text-xs text-wt-text-muted">Max cashback (0 = no cap)</Label>
              <Input
                type="number" min="0"
                value={settings.cashback_max ?? ''}
                onChange={(e) => setSettings(prev => ({ ...prev, cashback_max: e.target.value }))}
                className="mt-1"
                data-testid="cashback-max-input"
              />
            </div>
          </div>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-wt-surface border border-wt-border rounded-xl p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Settings className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-wt-text-primary">Module Settings</h2>
            <p className="text-sm text-wt-text-muted">Enable or disable tracking page features</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-wt-bg rounded-lg">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-400" />
              <div>
                <p className="font-medium text-wt-text-primary">Order Timeline</p>
                <p className="text-sm text-wt-text-muted">Show order status progression</p>
              </div>
            </div>
            <Switch
              checked={settings.timeline_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, timeline_enabled: checked }))}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-wt-bg rounded-lg">
            <div className="flex items-center gap-3">
              <Map className="w-5 h-5 text-green-400" />
              <div>
                <p className="font-medium text-wt-text-primary">Live Map Tracking</p>
                <p className="text-sm text-wt-text-muted">Show driver location on map</p>
              </div>
            </div>
            <Switch
              checked={settings.map_tracking_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, map_tracking_enabled: checked }))}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-wt-bg rounded-lg">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-orange-400" />
              <div>
                <p className="font-medium text-wt-text-primary">Driver Details</p>
                <p className="text-sm text-wt-text-muted">Show driver name, photo, contact</p>
              </div>
            </div>
            <Switch
              checked={settings.driver_details_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, driver_details_enabled: checked }))}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-wt-bg rounded-lg">
            <div className="flex items-center gap-3">
              <Coins className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="font-medium text-wt-text-primary">Tip Module</p>
                <p className="text-sm text-wt-text-muted">Allow customers to tip drivers</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!stripeStatus.configured && settings.tip_enabled && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  Stripe Required
                </Badge>
              )}
              <Switch
                checked={settings.tip_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, tip_enabled: checked }))}
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-wt-bg rounded-lg">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-5 h-5 text-pink-400" />
              <div>
                <p className="font-medium text-wt-text-primary">Cross-sell Products</p>
                <p className="text-sm text-wt-text-muted">Show recommended products</p>
              </div>
            </div>
            <Switch
              checked={settings.crosssell_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, crosssell_enabled: checked }))}
            />
          </div>
          
          {/* Celebration Animation Toggle */}
          <div className="flex items-center justify-between p-4 bg-wt-bg rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎉</span>
              <div>
                <p className="font-medium text-wt-text-primary">Order Celebration</p>
                <p className="text-sm text-wt-text-muted">Confetti & sound for new orders</p>
              </div>
            </div>
            <Switch
              checked={settings.celebration_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, celebration_enabled: checked }))}
            />
          </div>
        </div>
      </motion.div>
      
      {/* Tip Module Settings */}
      {settings.tip_enabled && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Coins className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-wt-text-primary">Tip Settings</h2>
              <p className="text-sm text-wt-text-muted">Customize tip amounts and currency</p>
            </div>
          </div>
          
          <div className="grid gap-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Currency Code</Label>
              <Input
                value={settings.tip_currency || 'AED'}
                onChange={(e) => setSettings(prev => ({ ...prev, tip_currency: e.target.value.toUpperCase() }))}
                placeholder="AED"
                maxLength={3}
                className="bg-wt-bg border-wt-border text-wt-text-primary uppercase"
                data-testid="tip-currency-input"
              />
              <p className="text-xs text-wt-text-muted">e.g., AED, USD, INR, EUR</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Tip Preset Amounts</Label>
              <Input
                value={Array.isArray(settings.tip_slabs) ? settings.tip_slabs.join(', ') : settings.tip_slabs}
                onChange={(e) => {
                  const value = e.target.value;
                  // Parse comma-separated numbers
                  const slabs = value.split(',')
                    .map(s => s.trim())
                    .filter(s => s !== '')
                    .map(s => parseInt(s, 10))
                    .filter(n => !isNaN(n) && n > 0);
                  setSettings(prev => ({ ...prev, tip_slabs: slabs.length > 0 ? slabs : [5, 10, 20, 50] }));
                }}
                placeholder="5, 10, 20, 50"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="tip-slabs-input"
              />
              <p className="text-xs text-wt-text-muted">Comma-separated amounts shown as quick tip buttons</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Payment Gateway</Label>
              <select
                value={settings.tip_payment_gateway || 'stripe'}
                onChange={(e) => setSettings(prev => ({ ...prev, tip_payment_gateway: e.target.value }))}
                className="w-full h-10 rounded-md border border-wt-border bg-wt-bg text-wt-text-primary px-3"
                data-testid="tip-payment-gateway-select"
              >
                <option value="stripe">Stripe</option>
                <option value="mamopay">MamoPay</option>
              </select>
              <p className="text-xs text-wt-text-muted">Gateway used for collecting tips</p>
            </div>
          </div>
          
          {/* Tip Preview */}
          <div className="mt-6 pt-4 border-t border-wt-border">
            <Label className="text-wt-text-primary mb-3 block">Preview</Label>
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(settings.tip_slabs) ? settings.tip_slabs : [5, 10, 20, 50]).map((amount, idx) => (
                <div
                  key={idx}
                  className="px-4 py-2 rounded-full border border-emerald-500/50 text-emerald-400 text-sm"
                >
                  {settings.tip_currency || 'AED'} {amount}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Cross-sell Products Section */}
      {settings.crosssell_enabled && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-500/20 rounded-lg">
                <ShoppingBag className="w-5 h-5 text-pink-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-wt-text-primary">Cross-sell Products</h2>
                <p className="text-sm text-wt-text-muted">Select products to recommend</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowProductModal(true)}
              className="border-wt-border text-wt-text-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Products
            </Button>
          </div>
          
          {/* Cross-sell Headline */}
          <div className="mb-6 space-y-2">
            <Label className="text-wt-text-primary">Section Headline</Label>
            <Input
              value={settings.crosssell_headline}
              onChange={(e) => setSettings(prev => ({ ...prev, crosssell_headline: e.target.value }))}
              placeholder="You Might Also Like"
              className="bg-wt-bg border-wt-border text-wt-text-primary"
            />
            <p className="text-xs text-wt-text-muted">Custom title displayed above products</p>
          </div>
          
          {/* Cross-sell Timer */}
          <div className="mb-6 p-4 bg-wt-bg rounded-lg border border-wt-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="font-medium text-wt-text-primary">Urgency Timer</p>
                  <p className="text-sm text-wt-text-muted">Add countdown timer to create urgency</p>
                </div>
              </div>
              <Switch
                checked={settings.crosssell_timer_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, crosssell_timer_enabled: checked }))}
              />
            </div>
            {settings.crosssell_timer_enabled && (
              <div className="mt-3 pt-3 border-t border-wt-border">
                <Label className="text-wt-text-primary text-sm">Timer Duration (minutes per session)</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="number"
                    min="5"
                    max="120"
                    value={settings.crosssell_timer_duration}
                    onChange={(e) => setSettings(prev => ({ ...prev, crosssell_timer_duration: parseInt(e.target.value) || 30 }))}
                    className="w-24 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                  <span className="text-sm text-wt-text-muted">minutes</span>
                </div>
                <p className="text-xs text-wt-text-muted mt-2">Timer resets each time customer opens the tracking page</p>
              </div>
            )}
          </div>
          
          {settings.crosssell_product_ids.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {settings.crosssell_product_ids.map((productId) => {
                const product = selectedProductDetails.find(p => p.id === productId);
                return (
                  <div key={productId} className="relative bg-wt-bg rounded-lg p-3 group">
                    <button
                      onClick={() => handleRemoveProduct(productId)}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                    <div className="aspect-square bg-slate-700 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
                      {product?.image_url ? (
                        <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
                      ) : (
                        <ShoppingBag className="w-8 h-8 text-gray-500" />
                      )}
                    </div>
                    <p className="text-xs text-wt-text-primary truncate">{product?.title || productId}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {product?.compare_at_price && product.compare_at_price > product.price && (
                        <span className="text-xs text-gray-500 line-through">{formatCurrency(product.compare_at_price)}</span>
                      )}
                      {product?.price && (
                        <span className="text-xs text-wt-gold font-semibold">{formatCurrency(product.price)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-wt-text-muted">
              <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No products selected</p>
              <p className="text-sm">Click "Add Products" to select items</p>
            </div>
          )}
        </motion.div>
      )}
      
      {/* Payment Gateway Status for Tips */}
      {settings.tip_enabled && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-wt-text-primary">
                  {settings.tip_payment_gateway === 'mamopay' ? 'MamoPay' : 'Stripe'} Integration
                </h2>
                <p className="text-sm text-wt-text-muted">Required for tip payments via {settings.tip_payment_gateway === 'mamopay' ? 'MamoPay' : 'Stripe'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {settings.tip_payment_gateway === 'stripe' && (
                <>
                  <Badge className={stripeStatus.configured ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                    {stripeStatus.configured ? (
                      <><CheckCircle className="w-3 h-3 mr-1" /> Connected</>
                    ) : (
                      'Not Connected'
                    )}
                  </Badge>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/integrations/stripe')}
                    className="border-wt-border text-wt-text-primary"
                  >
                    {stripeStatus.configured ? 'Manage' : 'Connect'}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
              {settings.tip_payment_gateway === 'mamopay' && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/integrations')}
                  className="border-wt-border text-wt-text-primary"
                >
                  Configure MamoPay
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Product Selection Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Select Products for Cross-sell</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-wt-text-muted" />
              <Input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search products by name, SKU..."
                className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            {loadingProducts ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-wt-gold" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 overflow-y-auto flex-1 pr-2">
                {products.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => {
                      if (selectedProducts.includes(product.id)) {
                        setSelectedProducts(prev => prev.filter(id => id !== product.id));
                      } else {
                        setSelectedProducts(prev => [...prev, product.id]);
                      }
                    }}
                    className={`cursor-pointer rounded-lg p-3 border transition-colors ${
                      selectedProducts.includes(product.id)
                        ? 'border-wt-gold bg-wt-gold/10'
                        : 'border-wt-border bg-wt-bg hover:border-wt-gold/50'
                    }`}
                  >
                    <div className="aspect-square bg-slate-700 rounded-lg mb-2 overflow-hidden relative">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-10 h-10 text-gray-500" />
                        </div>
                      )}
                      {selectedProducts.includes(product.id) && (
                        <div className="absolute top-2 right-2 bg-wt-gold rounded-full p-1">
                          <CheckCircle className="w-4 h-4 text-black" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-wt-text-primary font-medium truncate">{product.title}</p>
                    {product.variant_title && product.variant_title !== 'Default Title' && (
                      <p className="text-xs text-wt-text-muted truncate">{product.variant_title}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        {product.compare_at_price && product.compare_at_price > product.price && (
                          <span className="text-xs text-gray-500 line-through">
                            {formatCurrency(product.compare_at_price)}
                          </span>
                        )}
                        <span className="text-sm text-wt-gold font-semibold">
                          {formatCurrency(product.price)}
                        </span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        product.quantity > 10 ? 'bg-green-500/20 text-green-400' :
                        product.quantity > 0 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {product.quantity > 0 ? `${product.quantity} in stock` : 'Out of stock'}
                      </span>
                    </div>
                    {product.sku && (
                      <p className="text-xs text-wt-text-muted mt-1">SKU: {product.sku}</p>
                    )}
                  </div>
                ))}
                {products.length === 0 && !loadingProducts && (
                  <div className="col-span-full text-center py-8 text-wt-text-muted">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No products found</p>
                    <p className="text-sm">Try adjusting your search</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-between items-center pt-4 border-t border-wt-border">
              <span className="text-sm text-wt-text-muted">
                {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedProducts([]);
                    setShowProductModal(false);
                  }}
                  className="border-wt-border text-wt-text-primary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddProducts}
                  disabled={selectedProducts.length === 0}
                  className="bg-wt-gold hover:bg-wt-gold-dark text-black"
                >
                  Add {selectedProducts.length} Product{selectedProducts.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrackingSettingsPage;
